import L from 'leaflet';
import { defineComponent, inject, nextTick, onMounted, ref, getCurrentInstance, provide, h, onBeforeUnmount, toRaw } from 'vue';
import { Functions, Utilities, InjectionKeys, LMap } from '@vue-leaflet/vue-leaflet';
import { Container, Renderer, Ticker } from 'pixi.js';
import 'leaflet-pixi-overlay';
import { PixiMarker } from '../PixiMarker';
import BezierEasing from 'bezier-easing';
import { PixiMarkerLayerInjectionKey } from '.';

const doubleBuffering = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any)['MSStream'];
const easing = BezierEasing(0, 0, 0.25, 1);
const frameRateInMilliseconds = 1000 / 60; // 60Hz = 1/60s = 16.66666...ms

export default defineComponent({
    name: 'PixiMarkerLayer',
    props: Functions.LayerGroup.layerGroupProps,
    setup(props, context) {
        const instance = getCurrentInstance();
        const parentContainer = instance?.parent?.proxy as InstanceType<typeof LMap>;

        let leafletObject: L.LayerGroup | undefined;
        const ready = ref(false);

        const useGlobalLeaflet = inject(InjectionKeys.UseGlobalLeafletInjection);
        const addLayer = Utilities.assertInject(InjectionKeys.AddLayerInjection);

        const { methods } = Functions.LayerGroup.setupLayerGroup(props, leafletObject, context);

        let pixiContainer: Container | undefined;
        let pixiOverlay: L.PixiOverlay | undefined;
        const ticker: Ticker = Ticker.shared;
        let utils = ref<L.PixiOverlayUtils>();
        const markers: Array<InstanceType<typeof PixiMarker>> = [];
        let markerClicked = false;
        let renderNextTick = false;
        let tooltipTimeout: ReturnType<typeof setTimeout>;

        function scaleMultiplier(zoom: number) {
            const minZoom = 11;
            const maxZoom = 15;
            if (zoom < minZoom) return 0.2;
            if (zoom >= maxZoom) return 1;
            return 0.2 + (zoom - minZoom) / (maxZoom - minZoom);
        }

        function getScale(zoom: number) {
            if (!utils.value) return 0;
            return Math.round(((1 / utils.value.getScale(zoom)) * scaleMultiplier(zoom) + Number.EPSILON) * 100000) / 100000;
        }

        function getTooltipOffset(zoom: number) {
            return new L.Point(0, -Math.round(5 + zoom * scaleMultiplier(zoom)));
        }

        async function initPixi() {
            pixiContainer = new Container();
            pixiContainer.sortableChildren = true;
            pixiContainer.interactiveChildren = true;
            pixiContainer.eventMode = 'dynamic';

            // add pixi event handlers
            pixiContainer.on('click', e => e.stopPropagation());

            let zoomChangeTs: number | null = null;

            pixiOverlay = (() =>
                L.pixiOverlay(
                    (_utils, event) => {
                        utils.value = _utils;
                        const container: Container = _utils.getContainer();
                        const renderer: Renderer = _utils.getRenderer();

                        if (event.type === 'add') {
                            // attach a listener that reloads the layer when
                            // webglcontextlost is detected.
                            renderer.view.addEventListener?.('webglcontextlost', () => {
                                context.emit('error', 'WebGL context was lost. Reloading...');
                                reload();
                            });
                        } else if (event.type === 'moveend') {
                            zoomChangeTs = 0;
                            const zoom = utils.value.getMap().getZoom();
                            const targetScale = getScale(zoom);

                            markers.forEach(marker => {
                                marker.currentScale = marker.getScale();
                                marker.targetScale = targetScale;
                            });
                            renderNextTick = true;
                            return;
                        } else if (event.type === 'redraw') {
                            const delta = event.delta;
                            const zoom = utils.value.getMap().getZoom();
                            const targetScale = getScale(zoom);

                            // process scaling animation
                            if (zoomChangeTs !== null) {
                                zoomChangeTs += delta;
                                let lambda = zoomChangeTs / frameRateInMilliseconds;
                                if (lambda > 1) {
                                    lambda = 1;
                                    zoomChangeTs = null;
                                }
                                lambda = easing(lambda);

                                markers.forEach(marker => {
                                    // fallback to calculated scale if targetScale is not set
                                    const newScale = marker.targetScale || targetScale;
                                    marker.setScale(marker.currentScale + lambda * (newScale - marker.currentScale));
                                });
                            } else {
                                // update scale if it does not match current scale
                                markers.forEach(marker => marker.getScale() !== targetScale && marker.setScale(targetScale));
                            }

                            // process movement animation
                            markers.forEach(marker => {
                                if (marker.moveChangeTs === null) return;
                                marker.moveChangeTs += delta;
                                let lambda = marker.moveChangeTs / frameRateInMilliseconds;
                                if (lambda > 1) {
                                    lambda = 1;
                                    marker.moveChangeTs = null;
                                }
                                lambda = easing(lambda);

                                if (!marker.targetLatlng) return;

                                const targetLatLng = L.latLng(marker.targetLatlng);
                                const currentLatLng = L.latLng(marker.currentLatlng);
                                const deltaLat = (targetLatLng.lat - currentLatLng.lat) * lambda;
                                const deltaLng = (targetLatLng.lng - currentLatLng.lng) * lambda;

                                const newLatLng = {
                                    lat: currentLatLng.lat + deltaLat,
                                    lng: currentLatLng.lng + deltaLng
                                };

                                marker.setPosition(newLatLng);
                            });
                        }

                        renderer.render(container);
                    },
                    pixiContainer,
                    {
                        doubleBuffering,
                        autoPreventDefault: false,
                        pane: 'markerPane',
                        projectionZoom: function (map: L.Map) {
                            // override so we have always valid values to use
                            let maxZoom = map.getMaxZoom();
                            if (maxZoom === Infinity) maxZoom = 20;

                            let minZoom = map.getMinZoom();
                            if (minZoom === 0) minZoom = 1;

                            return (maxZoom + minZoom) / 2;
                        }
                    }
                ))();

            // add leaflet layer
            parentContainer.leafletObject?.addLayer(pixiOverlay);

            // add leaflet event handlers
            parentContainer.leafletObject?.on('zoomanim', pixiOverlay.redraw, pixiOverlay);
            L.DomEvent.on((pixiOverlay as any)._pixiContainer, 'click', onLayerClick);

            // start ticker
            ticker.add(redraw);
        }

        function destroyPixi() {
            ready.value = false;

            // stop ticker
            ticker.remove(redraw);

            // remove leaflet event handlers
            L.DomEvent.off((pixiOverlay as any)?._pixiContainer, 'click', onLayerClick);

            parentContainer.leafletObject?.off('zoomanim', pixiOverlay?.redraw, pixiOverlay);

            // remove leaflet layer
            parentContainer.leafletObject?.removeLayer(pixiOverlay!);

            // destroy container
            pixiContainer?.destroy();
        }

        function reload() {
            destroyPixi();
            nextTick(() => {
                initPixi();
                ready.value = true;
            });
        }

        let renderFrameTimeout: ReturnType<typeof setTimeout>;
        function renderFrame() {
            // add small timeout to prevent flickering
            clearTimeout(renderFrameTimeout);
            renderFrameTimeout = setTimeout(() => (renderNextTick = true), frameRateInMilliseconds);
        }

        function redraw(delta: number) {
            if (!pixiOverlay) return;

            if (renderNextTick) {
                renderNextTick = false;
                pixiOverlay.redraw({ type: 'redraw', delta });
            }
        }

        function addMarker(marker: InstanceType<typeof PixiMarker>) {
            if (!pixiContainer) return;

            markers.push(marker);
            pixiContainer.addChild(toRaw(marker.sprite) as Container);

            renderFrame();
        }

        function removeMarker(marker: InstanceType<typeof PixiMarker>) {
            if (!pixiContainer || !marker) return;

            // if sprite exists, remove from container
            if (marker.sprite) pixiContainer.removeChild(toRaw(marker.sprite) as Container);

            // remove PixiMarker instance from markers collection
            const index = markers.findIndex(x => x.id === marker.id);
            if (index !== -1) markers.splice(index, 1);

            renderFrame();
        }

        function raiseMarkerClickedEvent() {
            markerClicked = true;
        }

        function onLayerClick(e: Event) {
            if (markerClicked) e.stopPropagation();
            markerClicked = false;
        }

        function showTooltip(latLng: L.LatLngExpression, tooltipContent: string) {
            if (tooltipTimeout) clearTimeout(tooltipTimeout);

            // check if leaflet object is ready
            if (!leafletObject) return;

            // close tooltip if it is open
            if (leafletObject.isTooltipOpen()) leafletObject.closeTooltip();

            // set tooltip content
            leafletObject.setTooltipContent(tooltipContent);

            // set tooltip offset
            const tooltip = leafletObject.getTooltip();
            if (tooltip) tooltip.options.offset = [0, -30];

            // open tooltip
            if (!leafletObject.isTooltipOpen()) leafletObject.openTooltip(latLng);
        }

        function hideTooltip(delay = 0) {
            if (!leafletObject?.isTooltipOpen()) return;
            tooltipTimeout = setTimeout(() => {
                leafletObject?.closeTooltip();
                leafletObject?.setTooltipContent('');
            }, delay);
        }

        function moveTooltip(latLng: L.LatLngExpression) {
            if (!leafletObject?.isTooltipOpen()) return;
            leafletObject?.getTooltip()?.setLatLng(latLng);
        }

        function updateTooltipOffset() {
            const tooltip = leafletObject?.getTooltip();
            if (!tooltip) return;

            // reset tooltip offset
            tooltip.options.offset = getTooltipOffset(parentContainer?.leafletObject?.getZoom() ?? 0);
        }

        provide(PixiMarkerLayerInjectionKey, {
            utils,
            addMarker,
            removeMarker,
            renderFrame,
            showTooltip,
            hideTooltip,
            moveTooltip,
            raiseMarkerClickedEvent
        });

        onMounted(async () => {
            initPixi();

            const { layerGroup }: typeof L = useGlobalLeaflet ? Utilities.WINDOW_OR_GLOBAL.L : await import('leaflet');
            leafletObject = layerGroup([pixiOverlay!], props.options);

            const { listeners } = Utilities.remapEvents(context.attrs);
            leafletObject.on(listeners);

            Utilities.propsBinder(methods, leafletObject, props);
            addLayer({
                ...props,
                ...methods,
                leafletObject
            });

            // add tooltip
            leafletObject?.bindTooltip('', {
                direction: 'top',
                offset: getTooltipOffset(parentContainer.leafletObject?.getZoom() ?? 0)
            });
            parentContainer.leafletObject?.on('zoomend', updateTooltipOffset);

            ready.value = true;
            nextTick(() => context.emit('ready', leafletObject));
        });

        onBeforeUnmount(() => {
            parentContainer.leafletObject?.off('zoomend', updateTooltipOffset);
            destroyPixi();
        });

        return { ready, leafletObject };
    },
    render() {
        return this.ready ? h('div', { class: 'pixi-marker-layer' }, [this.$slots.default?.()]) : null;
    }
});
